import { useMemo, useState } from "react";
import type { EChartsOption } from "echarts";
import { Chart } from "./Chart.tsx";
import { useDepthBucketsSeries } from "../hooks/useDepthBucketsSeries.ts";
import {
  toDepthSeries,
  allEmpty,
  BAND_PCT,
  type Side,
  type BandKey,
} from "../lib/depthSeries.ts";

const MUTED = "#8a93a6";
const TEXT = "#cfe0ff";
const GRID = "rgba(255,255,255,0.08)";

// SIDE = color family (non-status hues so green=ok / amber=caution is never
// hijacked): bid = blue, ask = violet. BAND = light -> dark shade within each
// side (deeper into the book = darker). All solid.
const SIDE_BAND_COLOR: Record<Side, Record<BandKey, string>> = {
  bid: { "0p25": "#d5e6ff", "0p5": "#9ec5ff", "1p0": "#5b8def", "2p0": "#2f5bb0" },
  ask: { "0p25": "#e8dcfb", "0p5": "#c9aef5", "1p0": "#9b6fe0", "2p0": "#6d3fb0" },
};
function colorFor(side: Side, band: BandKey): string {
  return SIDE_BAND_COLOR[side][band];
}
// One legend toggle per band+family controls BOTH sides: bid and ask series
// share the same ECharts series name, and same-name series share one legend
// chip and toggle together. Side identity stays in line color (bid blue /
// ask violet, see the caption); the tooltip labels bid/ask per line.
function pairedName(band: BandKey, family: "quoted" | "exec"): string {
  return family === "exec" ? `exec ±${BAND_PCT[band]}` : `±${BAND_PCT[band]}`;
}

function fmtK(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(2) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "k";
  return n.toFixed(0);
}

// all ticks are HH:MM (UTC); the date is shown once as a caption under the
// chart, so the axis never wedges a lone bare date between hours.
function hhmm(ms: number): string {
  const d = new Date(ms);
  return `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
}

function dateRangeLabel(startMs: number, endMs: number): string {
  const f = (ms: number) => {
    const d = new Date(ms);
    return `${d.getUTCDate()} ${d.toLocaleString("en-US", { month: "short", timeZone: "UTC" })}`;
  };
  const a = f(startMs);
  const b = f(endMs);
  return a === b ? `${a} UTC` : `${a} – ${b} UTC`;
}

// default applied only when the user has not yet touched the legend.
const DEFAULT_SELECTED: Record<string, boolean> = {
  // paired toggles: one entry drives BOTH sides of the band (same-name rule).
  // exec ±2% on by default so the quoted-vs-exec divergence is visible on
  // first load — the point of the metric (LIQ-29).
  "±0.25%": false, "±0.5%": false, "±1%": false, "±2%": true,
  "exec ±0.25%": false, "exec ±0.5%": false, "exec ±1%": false,
  "exec ±2%": true,
};

export function DepthChart({
  venue,
  pair,
  clientId,
  lookbackMinutes,
  bucketSeconds,
  quoteAsset,
}: {
  venue: string;
  pair: string;
  clientId?: string;
  lookbackMinutes: number;
  bucketSeconds: number;
  quoteAsset?: string;
}) {
  const q = useDepthBucketsSeries({
    venue,
    pair,
    clientId,
    lookbackMinutes,
    bucketSeconds,
  });

  // Persisted legend selection: authoritative React state, updated by ECharts'
  // legendselectchanged, and fed INTO the option (in deps) so a window change
  // rebuilds with the CURRENT selection rather than the static default. This is
  // correct even though the Chart wrapper uses notMerge.
  const [selected, setSelected] = useState<Record<string, boolean>>(DEFAULT_SELECTED);
  const onEvents = useMemo(
    () => ({
      legendselectchanged: (e: { selected: Record<string, boolean> }) =>
        setSelected({ ...e.selected }),
    }),
    [],
  );

  const result = useMemo(
    () =>
      toDepthSeries(q.rows, {
        lookbackMinutes,
        bucketSeconds,
        nowMs: Date.now(),
      }),
    [q.rows, lookbackMinutes, bucketSeconds],
  );

  // Axis left edge = first actual bucket (clamped to window start), so the line
  // begins at the y-axis instead of floating mid-axis when the table holds less
  // history than the window spans.
  const firstTs = useMemo(() => {
    let min = Infinity;
    for (const s of result.series) {
      for (const [ts, v] of s.points) {
        if (v != null && ts < min) min = ts;
      }
    }
    return Number.isFinite(min) ? min : result.windowStartMs;
  }, [result]);
  const axisMin = Math.max(result.windowStartMs, firstTs);

  // Mirrored layout: bids plot positive (up from the center line), asks plot
  // NEGATED (down). Shared symmetric y-scale so the two halves are a true
  // magnitude comparison — a balanced book is symmetric, an imbalanced one is
  // visibly lopsided. The negation is purely presentational; depthSeries stays
  // a pure data transform.
  const maxAbs = useMemo(() => {
    let m = 0;
    for (const s of result.series) {
      for (const [, v] of s.points) {
        if (v != null && v > m) m = v;
      }
    }
    return m > 0 ? m : 1;
  }, [result]);

  const showSymbols = result.populatedBuckets <= 4;

  const option = useMemo<EChartsOption>(() => {
    return {
      backgroundColor: "transparent",
      textStyle: { color: MUTED },
      // top 44: clears the single-row paired legend + the y-axis name.
      grid: { left: 56, right: 16, top: 44, bottom: 28 },
      legend: {
        top: 0,
        textStyle: { color: MUTED, fontSize: 11 },
        inactiveColor: "rgba(255,255,255,0.18)",
        selected,
      },
      tooltip: {
        trigger: "axis",
        backgroundColor: "rgba(20,24,33,0.95)",
        borderColor: GRID,
        textStyle: { color: TEXT, fontSize: 12 },
        // paired series share a name, so each line is disambiguated bid/ask
        // from the render order (result.series); ask values are un-negated.
        formatter: (params: unknown) => {
          const list = (Array.isArray(params) ? params : [params]) as Array<{
            axisValueLabel?: string;
            marker?: string;
            seriesIndex?: number;
            seriesName?: string;
            value?: [number, number | null];
          }>;
          const head = list[0]?.axisValueLabel ?? "";
          const lines = list.map((p) => {
            const side = result.series[p.seriesIndex ?? 0]?.side ?? "";
            const v = p.value?.[1];
            const val = v == null ? "—" : fmtK(Math.abs(Number(v)));
            return `${p.marker ?? ""} ${side} ${p.seriesName ?? ""}&nbsp;&nbsp;<b>${val}</b>`;
          });
          return [head, ...lines].join("<br/>");
        },
      },
      xAxis: {
        type: "time",
        min: axisMin,
        max: result.windowEndMs,
        position: "bottom",
        axisLine: { lineStyle: { color: GRID } },
        axisLabel: {
          color: MUTED,
          fontSize: 11,
          formatter: (value: number) => hhmm(value),
        },
        splitLine: { show: false },
      },
      yAxis: {
        type: "value",
        name: `${quoteAsset ?? ""} notional`,
        nameTextStyle: { color: MUTED, fontSize: 11 },
        min: -maxAbs,
        max: maxAbs,
        // both halves read as positive magnitudes (abs); the sign is layout only
        axisLabel: {
          color: MUTED,
          fontSize: 11,
          formatter: (v: number) => fmtK(Math.abs(v)),
        },
        splitLine: { lineStyle: { color: GRID } },
      },
      series: result.series.map((s) => {
        const color = colorFor(s.side, s.band);
        const sign = s.side === "ask" ? -1 : 1;
        const data: [number, number | null][] = s.points.map(([ts, v]) => [
          ts,
          v == null ? null : v * sign,
        ]);
        return {
          name: pairedName(s.band, s.family),
          type: "line" as const,
          showSymbol: showSymbols,
          symbolSize: 5,
          connectNulls: false,
          // solid = quoted (touch-anchored), dashed = executable (mid-anchored)
          lineStyle: {
            width: 2,
            color,
            type: (s.family === "exec" ? "dashed" : "solid") as "dashed" | "solid",
          },
          itemStyle: { color },
          data,
          // the center divider: draw the zero line once (on the first series)
          ...(s.family === "quoted" && s.side === "bid" && s.band === "2p0"
            ? {
                markLine: {
                  silent: true,
                  symbol: "none",
                  lineStyle: { color: "rgba(255,255,255,0.25)", width: 1, type: "solid" as const },
                  label: { show: false },
                  data: [{ yAxis: 0 }],
                },
              }
            : {}),
        };
      }),
    };
  }, [result, quoteAsset, showSymbols, selected, axisMin, maxAbs]);

  if (q.isError) {
    return <div style={{ fontSize: 12, color: "#e0a458" }}>could not load depth series</div>;
  }
  if (allEmpty(result)) {
    return (
      <div style={{ fontSize: 13, color: MUTED, padding: "24px 2px", lineHeight: 1.5 }}>
        No depth buckets in this window yet.
      </div>
    );
  }

  return (
    <div>
      <Chart option={option} height={360} onEvents={onEvents} />
      <div style={{ fontSize: 11, color: MUTED, marginTop: 4 }}>
        {dateRangeLabel(axisMin, result.windowEndMs)} · {result.okBuckets}/
        {result.populatedBuckets} buckets ok · 5-minute buckets ·{" "}
        <span style={{ color: SIDE_BAND_COLOR.bid["2p0"] }}>bid ↑</span> /{" "}
        <span style={{ color: SIDE_BAND_COLOR.ask["2p0"] }}>ask ↓</span> · darker =
        deeper · solid = quoted (±% from same-side touch) · dashed = exec (±%
        of mid) · toggle in the legend
      </div>
    </div>
  );
}

export type { Side, BandKey };
